/**
 * guitar-tuner-core.js — the part of /guitar-tuner that decides what the page
 * SAYS: which string is sounding, how far it is from its note, and whether the
 * page may tell the person to turn the peg at all.
 *
 * WHY THE LAST PART EXISTS
 * ------------------------
 * A tuner's instruction is physical. «Подтянуть» on a string that is already
 * at its note raises its tension; followed for long enough it breaks the
 * string or lifts the bridge. Report #1788 (29.09.2026, iPhone): «гитара по
 * швам чуть не лопнула, очень перетягивать просит». Reproduced on prod: the
 * string cards were numbered backwards (the card «6» was the THIN E4), a tap
 * locks the tuner on the tapped card, and a correctly tuned thick E then read
 * «−2390¢ · подтянуть → E4» — two octaves, with nothing on screen doubting it.
 *
 * So every instruction below is gated by what a guitar can physically be:
 *   · auto mode tells to tighten by at most a semitone and a half, and less
 *     where strings are close. A string further below the nearest note may
 *     be the lower neighbour tuned sharp, and
 *     auto mode would walk it up to the neighbour's note (matrix 30.09: a
 *     string 300 ¢ sharp got «подтянуть → next string» in 83 % of frames);
 *   · a locked string that hears ANOTHER string's note says so and offers
 *     the switch instead of an instruction;
 *   · a reading too far from the locked note to be a detuned version of it
 *     says so instead of an instruction.
 * Loosening never damages anything, so it is allowed within reach.
 *
 * DETECTION
 * ---------
 * NSDF (McLeod) computed through the shared FFT (public/js/dsp-fft.js,
 * window.DspFft) — the old per-lag autocorrelation spent tens of ms on every
 * animation frame. The frame is chosen by frameSize(): 85 ms holds seven
 * periods of the low E. Thresholds were chosen on REAL plucked strings —
 * EGFxSet (Zenodo 7044411, CC BY 4.0) and NSynth guitar_acoustic (CC BY 4.0)
 * through several microphone models; see the numbers next to each constant.
 *
 * No DOM, no Web Audio: tests/js/guitar-tuner-core.test.mjs drives it with
 * Float32Arrays.
 */
(function (root, factory) {
    var api = factory();
    if (typeof window !== 'undefined') window.GuitarTunerCore = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
}(this, function () {
    'use strict';

    // ── Notes and tunings ───────────────────────────────────────────────
    var SHARPS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    var FLATS = ['C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B'];

    /* MIDI notes from string 6 (thickest) to string 1 (thinnest). A string's
       NUMBER comes from its position here and nowhere else — the old table
       ran the other way and labelled cards with 6-index arithmetic, which is
       how the card «6» ended up on the thin E. */
    var TUNINGS = {
        standard: { midi: [40, 45, 50, 55, 59, 64] },
        dropD: { midi: [38, 45, 50, 55, 59, 64] },
        openG: { midi: [38, 43, 50, 55, 59, 62] },
        openD: { midi: [38, 45, 50, 54, 57, 62] },
        dadgad: { midi: [38, 45, 50, 55, 57, 62] },
        halfStep: { midi: [39, 44, 49, 54, 58, 63], flats: true }
    };

    function midiToFreq(m, a4) { return (a4 || 440) * Math.pow(2, (m - 69) / 12); }
    function freqToMidi(f, a4) { return 69 + 12 * Math.log2(f / (a4 || 440)); }
    function cents(f, ref) { return 1200 * Math.log2(f / ref); }
    function noteName(midi, flats) {
        var n = Math.round(midi);
        return { name: (flats ? FLATS : SHARPS)[((n % 12) + 12) % 12], oct: Math.floor(n / 12) - 1, midi: n };
    }

    /** Strings of a tuning, thickest first: [{num:6, midi, name, oct, freq}, …, {num:1, …}]. */
    function strings(key, a4) {
        var t = TUNINGS[key] || TUNINGS.standard, out = [];
        for (var i = 0; i < t.midi.length; i++) {
            var nn = noteName(t.midi[i], t.flats);
            out.push({ num: t.midi.length - i, midi: t.midi[i], name: nn.name, oct: nn.oct, freq: midiToFreq(t.midi[i], a4) });
        }
        return out;
    }
    function usesFlats(key) { return !!(TUNINGS[key] && TUNINGS[key].flats); }

    /** Analysis frame: the power of two holding ≥ 85 ms (4096 at 44.1/48 kHz). */
    function frameSize(sampleRate) {
        return Math.pow(2, Math.ceil(Math.log2(Math.max(1024, sampleRate * 0.085))));
    }

    // ── Detector: NSDF through the FFT ──────────────────────────────────
    /**
     * new Detector({ sampleRate, size, fft })  — fft: window.DspFft ({fft, makeTwiddles});
     * without it the same sums are taken directly (slower, identical result).
     * analyze(frame) → { rms, tail, freq (0 = no pitch), clarity }
     *   rms  — the whole frame; tail — its newest quarter (≈21 ms), for onsets.
     */
    function Detector(opts) {
        opts = opts || {};
        this.sr = opts.sampleRate;
        this.n = opts.size || frameSize(opts.sampleRate);
        this.fmin = opts.fmin || 55;           // drop C's low C is 65.4 Hz
        this.fmax = opts.fmax || 1400;
        this.k = opts.k != null ? opts.k : 0.8;
        this.octaveMargin = opts.octaveMargin != null ? opts.octaveMargin : 0.04;
        this.floor = opts.floor != null ? opts.floor : 1e-4;
        this.maxTau = Math.min(Math.floor(this.sr / this.fmin), this.n - 2);
        this.minTau = Math.max(2, Math.floor(this.sr / this.fmax));
        this.x = new Float64Array(this.n);
        this.nsdf = new Float64Array(this.maxTau + 2);
        var F = opts.fft || null;
        if (F && typeof F.fft === 'function' && typeof F.makeTwiddles === 'function') {
            this.F = F;
            this.m = Math.pow(2, Math.ceil(Math.log2(2 * this.n)));
            this.re = new Float64Array(this.m);
            this.im = new Float64Array(this.m);
            this.tw = F.makeTwiddles(this.m);
        } else this.F = null;
    }

    Detector.prototype._acf = function (x) {
        var n = this.n, top = this.maxTau + 1, out = this.nsdf, tau, j;
        if (this.F) {
            var re = this.re, im = this.im, m = this.m;
            for (j = 0; j < m; j++) { re[j] = j < n ? x[j] : 0; im[j] = 0; }
            this.F.fft(re, im, this.tw);
            for (j = 0; j < m; j++) { re[j] = re[j] * re[j] + im[j] * im[j]; im[j] = 0; }
            this.F.fft(re, im, this.tw);       // the power spectrum is real and even: forward = inverse × m
            for (tau = 0; tau <= top; tau++) out[tau] = re[tau] / m;
        } else {
            for (tau = 0; tau <= top; tau++) {
                var s = 0; for (j = 0; j + tau < n; j++) s += x[j] * x[j + tau];
                out[tau] = s;
            }
        }
    };

    Detector.prototype.analyze = function (frame) {
        var n = this.n, x = this.x, i;
        var res = { rms: 0, tail: 0, freq: 0, clarity: 0 };
        if (!frame || frame.length < n) return res;
        var off = frame.length - n, mean = 0;
        for (i = 0; i < n; i++) mean += frame[off + i];
        mean /= n;
        var sq = 0, tq = 0, t0 = n - (n >> 2);
        for (i = 0; i < n; i++) { var v = frame[off + i] - mean; x[i] = v; sq += v * v; if (i >= t0) tq += v * v; }
        res.rms = Math.sqrt(sq / n);
        res.tail = Math.sqrt(tq / (n - t0));
        if (res.rms < this.floor) return res;

        this._acf(x);
        var nsdf = this.nsdf, maxTau = this.maxTau, minTau = this.minTau, tau;
        var mm = 2 * sq;                        // Σ(x_j² + x_{j+τ}²), shrinking as τ grows
        for (tau = 0; tau <= maxTau + 1; tau++) {
            var r = nsdf[tau];
            nsdf[tau] = mm > 0 ? 2 * r / mm : 0;
            mm -= x[tau] * x[tau] + x[n - 1 - tau] * x[n - 1 - tau];
        }

        // key maxima — one per positive lobe, after a positive-going zero crossing
        var peaks = [], pos = false, curMax = -1, curPos = -1, globalMax = 0;
        for (tau = minTau + 1; tau <= maxTau; tau++) {
            if (nsdf[tau] > 0 && nsdf[tau - 1] <= 0) { pos = true; curMax = -1; curPos = -1; }
            else if (nsdf[tau] <= 0 && nsdf[tau - 1] > 0) { if (pos && curPos > 0) peaks.push(curPos); pos = false; }
            if (pos && nsdf[tau] > curMax) { curMax = nsdf[tau]; curPos = tau; }
        }
        if (pos && curPos > 0 && curPos < maxTau) peaks.push(curPos);
        for (i = 0; i < peaks.length; i++) if (nsdf[peaks[i]] > globalMax) globalMax = nsdf[peaks[i]];
        if (!peaks.length || globalMax <= 0) return res;

        var chosen = -1, cut = this.k * globalMax;
        for (i = 0; i < peaks.length; i++) if (nsdf[peaks[i]] >= cut) { chosen = peaks[i]; break; }
        if (chosen < 0) return res;

        /* Octave guard. Through a phone mic the low strings lose most of
           their fundamental; the NSDF then has a lobe at HALF the period
           nearly as tall as the true one, and the first-lobe rule reads the
           note an octave (or a twelfth) high. The true period is the lag
           whose multiple does not lose correlation: a lobe at 2× or 3× the
           chosen lag that is clearly taller means the chosen lag was a
           harmonic. */
        if (this.octaveMargin >= 0) {
            var best = chosen, bestV = nsdf[chosen];
            for (var mult = 2; mult <= 3; mult++) {
                var lo = chosen * mult * 0.97, hi = chosen * mult * 1.03;
                for (i = 0; i < peaks.length; i++) {
                    var p = peaks[i];
                    if (p >= lo && p <= hi && nsdf[p] > bestV + this.octaveMargin) { best = p; bestV = nsdf[p]; }
                }
            }
            chosen = best;
        }

        var y1 = nsdf[chosen - 1], y2 = nsdf[chosen], y3 = nsdf[chosen + 1];
        var den = y1 + y3 - 2 * y2, lag = chosen;
        if (den < 0) lag = chosen + 0.5 * (y1 - y3) / den;
        if (!(lag > 1)) return res;
        res.freq = this.sr / lag;
        res.clarity = Math.max(0, Math.min(1, y2));
        return res;
    };

    // ── Tracker: frames → one steady reading ────────────────────────────
    /**
     * new Tracker(opts); push(analysis, tMs) → { cents, freq, fresh, held } | null
     *   cents are relative to A4 = 440 (the caller applies its own A4).
     *
     *   · a pluck (the frame's newest 21 ms jump 6 dB) starts a new note: the
     *     readings of the previous one are dropped and the next `settleMs`
     *     ignored — those frames still hold the pick's noise;
     *   · the gate is RELATIVE: a reading counts while the string is above
     *     the room and within `spanDb` of this note's peak. The fixed 0.01 RMS
     *     gate of the old page blanked a quiet phone mic most of the time a
     *     string was still ringing;
     *   · the value shown is the median of the last `median` readings, in
     *     cents — an octave slip in one or two frames cannot move it;
     *   · within one pluck a JUMP of more than `jumpCents` is believed only
     *     after `confirm` agreeing frames. Turning a peg moves the pitch
     *     smoothly, frame by frame; a jump without a new pluck is the other
     *     strings ringing along, or the fading tail read an octave off
     *     (NSynth 030-064: the E4 tail read E3 for 150 ms and walked the
     *     median there);
     *   · small moves of the median (< `emaSpan`) are eased by `ema`: on a
     *     quiet phone mic a tuned string showed «в строе» in 77.7 % of frames
     *     without it and 86.3 % with 0.2 (matrix 30.09); bigger moves — a peg
     *     turned — are shown at once;
     *   · a note that stops being heard is HELD for `holdMs` (flag `held`)
     *     instead of flashing «—» between frames.
     */
    function Tracker(opts) {
        opts = opts || {};
        this.size = opts.median || 5;
        this.settleMs = opts.settleMs != null ? opts.settleMs : 60;
        this.holdMs = opts.holdMs != null ? opts.holdMs : 1500;
        this.spanDb = opts.spanDb != null ? opts.spanDb : 36;
        this.minClarity = opts.minClarity != null ? opts.minClarity : 0.6;
        this.absFloor = opts.absFloor != null ? opts.absFloor : 3e-4;
        this.jumpCents = opts.jumpCents != null ? opts.jumpCents : 60;
        this.confirm = opts.confirm != null ? opts.confirm : 6;
        this.ema = opts.ema != null ? opts.ema : 0.2;
        this.emaSpan = opts.emaSpan != null ? opts.emaSpan : 20;
        this.reset();
    }
    Tracker.prototype.reset = function () {
        this.ring = []; this.cand = []; this.noise = null; this.peak = 0; this.tailLvl = 0;
        this.onsetAt = -1e9; this.lastGood = -1e9; this.value = null;
    };
    Tracker.prototype.push = function (a, t) {
        var rms = a.rms, tail = a.tail;
        /* room level: falls to any quieter frame at once, rises ~1.5 dB/s —
           slow enough that a ringing string never becomes «the room» */
        if (this.noise === null) this.noise = Math.min(rms, this.absFloor); // a string already ringing is not the room
        else if (rms < this.noise) this.noise = rms;
        else this.noise *= 1.003;
        if (tail > 2 * this.tailLvl && tail > 4 * this.noise && tail > this.absFloor) {
            this.onsetAt = t; this.peak = rms; this.ring = []; this.cand = [];
        }
        this.tailLvl = this.tailLvl ? this.tailLvl * 0.5 + tail * 0.5 : tail;
        if (rms > this.peak) this.peak = rms;

        var audible = rms > this.absFloor && rms > 2.5 * this.noise && rms > this.peak * Math.pow(10, -this.spanDb / 20);
        if (a.freq > 0 && a.clarity >= this.minClarity && audible && t - this.onsetAt >= this.settleMs) {
            var c = 1200 * Math.log2(a.freq / 440);
            var here = this.ring.length ? medianOf(this.ring) : null;
            if (here !== null && Math.abs(c - here) > this.jumpCents) {
                // a jump inside one pluck: collect it, believe it only when it holds
                this.cand.push(c);
                if (this.cand.length > this.confirm) this.cand.shift();
                var cm = medianOf(this.cand), agree = 0;
                for (var i = 0; i < this.cand.length; i++) if (Math.abs(this.cand[i] - cm) <= this.jumpCents / 2) agree++;
                if (agree < this.confirm) return this._keep(t);
                this.ring = this.cand.slice(-this.size); this.cand = [];
            } else {
                this.cand = [];
                this.ring.push(c);
                if (this.ring.length > this.size) this.ring.shift();
            }
            // odd count → true median; even → median of the newest odd count
            var r = this.ring.length & 1 ? this.ring : this.ring.slice(1), med = medianOf(r);
            if (this.ema > 0 && this.value !== null && Math.abs(med - this.value) < this.emaSpan) this.value += this.ema * (med - this.value);
            else this.value = med;
            this.lastGood = t;
            return { cents: this.value, freq: 440 * Math.pow(2, this.value / 1200), fresh: true, held: false };
        }
        return this._keep(t);
    };
    Tracker.prototype._keep = function (t) {
        if (this.value !== null && t - this.lastGood <= this.holdMs)
            return { cents: this.value, freq: 440 * Math.pow(2, this.value / 1200), fresh: false, held: true };
        this.value = null; this.ring = []; this.cand = [];
        return null;
    };
    function medianOf(a) { var s = a.slice().sort(function (p, q) { return p - q; }); return s[s.length >> 1]; }

    // ── Advice ──────────────────────────────────────────────────────────
    /* Limits in cents.
       OK — the ±5 ¢ window of «в строе».
       AUTO_TIGHTEN_MAX, NEIGHBOUR_SHARP_MAX — auto mode does not know which
       string is sounding; a reading below the nearest note may be that
       string flat or the string BELOW it sharp — and «tighten» is harmless
       in the first case and the whole gap between the strings too much in
       the second (500 ¢ in most of standard tuning: the string breaks).
       Flat is the common state of a guitar (a semitone and a half after a
       season in the case), sharp by three semitones is not — that string is
       about to break anyway. So auto mode tells to tighten by up to a
       semitone and a half, and only while the reading cannot be the lower
       neighbour less than three semitones sharp. The gap matters: 400 ¢ G→B
       in standard (the B is told to tighten below 100 ¢ only), 300 ¢ B→D in
       open G and F#→A in open D (never), 200 ¢ G→A in DADGAD (never — lock
       the string). Closed loop 30.09 on real strings, 65 scenarios: a G
       string 250 ¢ sharp was told «−144¢ подтянуть → B3» and broke under a
       gap-blind 150 ¢ limit, and again under a 250 ¢ margin (its recording
       sits +6 ¢ over the nominal); with 300 ¢ it gets «далеко от строя» and
       no scenario breaks a string (prod: 10 of 65).
       Below the lowest string there is no neighbour on a six-string, so it
       may be told to tighten further — but only by AUTO_LOWEST_TIGHTEN_MAX:
       the low B of a seven-string sits a fourth under the low E, and auto
       mode must not walk it up there.
       LOCK_TIGHTEN_MAX / LOCK_LOOSEN_MAX — a locked string may be told to
       tighten by up to a fourth (a new string, a drop tuning raised back) and
       to loosen by up to 700 ¢; further away the sound is not a detuned
       version of the chosen string.
       OTHER_CAPTURE — a locked string that reads within a semitone of
       another string's note, and no closer to its own, is that other
       string — a semitone out of tune is plausible for any string.
       With ±50 ¢ a B string a semitone sharp, heard while the thin E was
       locked, read as «the E 400 ¢ slack — tighten» (matrix 30.09, 7 of 19
       real B notes). Ties go to «other»: in DADGAD the G and A strings are
       a whole tone apart and the middle is either of them.
       OCTAVE_FOLD — see advise(): a locked reading within 60 ¢ of the locked
       note one or two octaves off is folded onto it. */
    var LIMITS = { OK: 5, AUTO_TIGHTEN_MAX: 150, NEIGHBOUR_SHARP_MAX: 300, AUTO_LOWEST_TIGHTEN_MAX: 300, LOCK_TIGHTEN_MAX: 500, LOCK_LOOSEN_MAX: 700, OTHER_CAPTURE: 100, OCTAVE_FOLD: 60 };

    /**
     * advise(freq, strs, { locked, flats, a4 }) → {
     *   note:   {name, oct, midi, cents}   what is sounding (chromatic, vs A4)
     *   kind:   'ok' | 'tighten' | 'loosen' | 'far' | 'other'
     *   target: the string the reading is measured against (null: none)
     *   cents:  freq vs target, rounded
     *   other:  kind 'other' — the string that IS sounding
     * }
     * strs — from strings(); locked — a string NUMBER (1..6), or null = auto.
     */
    function advise(freq, strs, o) {
        o = o || {};
        var L = LIMITS, midi = freqToMidi(freq, o.a4 || 440), nn = noteName(midi, o.flats);
        var note = { name: nn.name, oct: nn.oct, midi: nn.midi, cents: Math.round((midi - nn.midi) * 100) };
        var nearest = null, nc = 0, lowest = null, i;
        for (i = 0; i < strs.length; i++) {
            var c = cents(freq, strs[i].freq);
            if (!nearest || Math.abs(c) < Math.abs(nc)) { nearest = strs[i]; nc = c; }
            if (!lowest || strs[i].freq < lowest.freq) lowest = strs[i];
        }
        var res = function (kind, target, cc, other) { return { note: note, kind: kind, target: target, cents: Math.round(cc), other: other || null }; };

        var target = null;
        if (o.locked != null) for (i = 0; i < strs.length; i++) if (strs[i].num === o.locked) target = strs[i];
        if (!target) {
            if (Math.abs(nc) < L.OK) return res('ok', nearest, nc);
            if (nc > 0) return res('loosen', nearest, nc);
            if (nearest === lowest) return res(-nc <= L.AUTO_LOWEST_TIGHTEN_MAX ? 'tighten' : 'far', nearest, nc);
            var below = null;                   // the next lower string of the tuning
            for (i = 0; i < strs.length; i++) if (strs[i].freq < nearest.freq && (!below || strs[i].freq > below.freq)) below = strs[i];
            var gap = cents(nearest.freq, below.freq);
            if (-nc <= L.AUTO_TIGHTEN_MAX + 0.5 && -nc < gap - L.NEIGHBOUR_SHARP_MAX - 0.5) return res('tighten', nearest, nc);
            return res('far', nearest, nc);
        }
        var tc = cents(freq, target.freq);
        // the other string nearest to the reading (the locked one excluded)
        var near2 = null, n2c = 0;
        for (i = 0; i < strs.length; i++) {
            if (strs[i] === target) continue;
            var c2 = cents(freq, strs[i].freq);
            if (!near2 || Math.abs(c2) < Math.abs(n2c)) { near2 = strs[i]; n2c = c2; }
        }
        if (near2 && Math.abs(n2c) < L.OTHER_CAPTURE + 0.5 && Math.abs(n2c) <= Math.abs(tc) + 0.5) return res('other', target, tc, near2);
        /* An octave off the locked note and near no other string: the
           detector's octave slip (a phone mic loses the low fundamental), not
           a string two times slacker. Cents are the same in every octave, so
           the instruction is folded onto the locked note and stays exact. The
           other-string check above runs first: E2 heard while E4 is locked is
           string 6, not E4 folded. */
        var k = Math.round(tc / 1200);
        if (k !== 0 && Math.abs(tc - 1200 * k) <= L.OCTAVE_FOLD) tc -= 1200 * k;
        if (Math.abs(tc) < L.OK) return res('ok', target, tc);
        if (tc < -L.LOCK_TIGHTEN_MAX || tc > L.LOCK_LOOSEN_MAX) return res('far', target, tc);
        return res(tc < 0 ? 'tighten' : 'loosen', target, tc);
    }

    return {
        VERSION: '2.0.0',
        TUNINGS: TUNINGS, LIMITS: LIMITS,
        midiToFreq: midiToFreq, freqToMidi: freqToMidi, cents: cents, noteName: noteName,
        strings: strings, usesFlats: usesFlats, frameSize: frameSize,
        Detector: Detector, Tracker: Tracker, advise: advise
    };
}));
